import React, { useState, useEffect } from 'react';
import {
  View,
  Text,
  StyleSheet,
  ScrollView,
  TouchableOpacity,
  Alert,
  ActivityIndicator,
} from 'react-native';
import { PaymentService } from '../services/paymentService';
import { AuthManager, User } from '../utils/authManager';

interface UserSubscriptionManagementScreenProps {
  onBack: () => void;
  onSubscriptionChange?: () => void;
}

interface UserSubscription {
  id: string;
  planId: string;
  planName: string;
  status: 'active' | 'cancelled' | 'past_due' | 'trialing' | 'incomplete';
  currentPeriodStart: string;
  currentPeriodEnd: string;
  cancelAtPeriodEnd: boolean;
  amount: number;
  currency: string;
  interval: string;
  nextBillingDate?: string;
  trialEnd?: string;
}

const UserSubscriptionManagementScreen: React.FC<UserSubscriptionManagementScreenProps> = ({
  onBack,
  onSubscriptionChange,
}) => {
  const [user, setUser] = useState<User | null>(null);
  const [subscription, setSubscription] = useState<UserSubscription | null>(null);
  const [loading, setLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState(false);

  useEffect(() => {
    loadUserData();
    loadSubscription();
  }, []);

  const loadUserData = async () => {
    try {
      const userData = await AuthManager.getSavedUser();
      setUser(userData);
    } catch (error) {
      console.error('Error loading user data:', error);
    }
  };

  const loadSubscription = async () => {
    try {
      setLoading(true);
      // In a real app, this would fetch from your backend
      // For demo purposes, we'll simulate a subscription
      const mockSubscription: UserSubscription = {
        id: 'sub_demo_123',
        planId: 'premium',
        planName: 'Premium',
        status: 'active',
        currentPeriodStart: '2024-01-01T00:00:00Z',
        currentPeriodEnd: '2024-02-01T00:00:00Z',
        cancelAtPeriodEnd: false,
        amount: 9.99,
        currency: 'usd',
        interval: 'month',
        nextBillingDate: '2024-02-01T00:00:00Z',
      };
      setSubscription(mockSubscription);
    } catch (error) {
      console.error('Error loading subscription:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleCancelSubscription = () => {
    Alert.alert(
      'Cancel Subscription',
      'Are you sure you want to cancel your subscription? You will continue to have access until the end of your current billing period.',
      [
        { text: 'Keep Subscription', style: 'cancel' },
        {
          text: 'Cancel Subscription',
          style: 'destructive',
          onPress: confirmCancelSubscription,
        },
      ]
    );
  };

  const confirmCancelSubscription = async () => {
    if (!subscription) return;

    setActionLoading(true);
    try {
      const result = await PaymentService.cancelSubscription(subscription.id);
      
      if (result.success) {
        Alert.alert(
          'Subscription Cancelled',
          'Your subscription has been cancelled. You will continue to have access until the end of your current billing period.',
          [{ text: 'OK', onPress: () => onSubscriptionChange?.() }]
        );
        // Update local state
        setSubscription(prev => prev ? { ...prev, cancelAtPeriodEnd: true } : null);
      } else {
        Alert.alert('Error', result.error || 'Failed to cancel subscription');
      }
    } catch (error) {
      Alert.alert('Error', 'An error occurred while cancelling your subscription');
    } finally {
      setActionLoading(false);
    }
  };

  const handleReactivateSubscription = async () => {
    if (!subscription) return;

    setActionLoading(true);
    try {
      // In a real app, this would call your backend to reactivate
      Alert.alert(
        'Subscription Reactivated',
        'Your subscription has been reactivated and will continue to renew automatically.',
        [{ text: 'OK', onPress: () => onSubscriptionChange?.() }]
      );
      // Update local state
      setSubscription(prev => prev ? { ...prev, cancelAtPeriodEnd: false } : null);
    } catch (error) {
      Alert.alert('Error', 'An error occurred while reactivating your subscription');
    } finally {
      setActionLoading(false);
    }
  };

  const handleUpgradePlan = () => {
    Alert.alert(
      'Upgrade Plan',
      'To upgrade your plan, please visit the subscription options screen.',
      [
        { text: 'Cancel', style: 'cancel' },
        { text: 'View Plans', onPress: () => {/* Navigate to subscription screen */} },
      ]
    );
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
    });
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'active':
        return '#34C759';
      case 'trialing':
        return '#FF9500';
      case 'past_due':
        return '#FF3B30';
      case 'cancelled':
        return '#8E8E93';
      default:
        return '#8E8E93';
    }
  };

  const getStatusText = (status: string) => {
    switch (status) {
      case 'active':
        return 'Active';
      case 'trialing':
        return 'Trial';
      case 'past_due':
        return 'Past Due';
      case 'cancelled':
        return 'Cancelled';
      case 'incomplete':
        return 'Incomplete';
      default:
        return status;
    }
  };

  if (loading) {
    return (
      <View style={styles.loadingContainer}>
        <ActivityIndicator size="large" color="#007AFF" />
        <Text style={styles.loadingText}>Loading subscription details...</Text>
      </View>
    );
  }

  return (
    <ScrollView style={styles.container}>
      <View style={styles.header}>
        <TouchableOpacity style={styles.backButton} onPress={onBack}>
          <Text style={styles.backButtonText}>← Back</Text>
        </TouchableOpacity>
        <Text style={styles.headerTitle}>Subscription Management</Text>
        <View style={{ width: 60 }} />
      </View>

      <View style={styles.content}>
        {!subscription ? (
          <View style={styles.noSubscriptionContainer}>
            <Text style={styles.noSubscriptionTitle}>No Active Subscription</Text>
            <Text style={styles.noSubscriptionText}>
              You don't have an active subscription. Choose a plan to get started.
            </Text>
            <TouchableOpacity style={styles.browsePlansButton}>
              <Text style={styles.browsePlansButtonText}>Browse Plans</Text>
            </TouchableOpacity>
          </View>
        ) : (
          <>
            {/* Subscription Overview */}
            <View style={styles.subscriptionCard}>
              <View style={styles.subscriptionHeader}>
                <Text style={styles.planName}>{subscription.planName} Plan</Text>
                <View style={[styles.statusBadge, { backgroundColor: getStatusColor(subscription.status) }]}>
                  <Text style={styles.statusText}>{getStatusText(subscription.status)}</Text>
                </View>
              </View>

              <View style={styles.subscriptionDetails}>
                <View style={styles.detailRow}>
                  <Text style={styles.detailLabel}>Amount</Text>
                  <Text style={styles.detailValue}>
                    ${subscription.amount.toFixed(2)} {subscription.currency.toUpperCase()}
                  </Text>
                </View>

                <View style={styles.detailRow}>
                  <Text style={styles.detailLabel}>Billing Cycle</Text>
                  <Text style={styles.detailValue}>
                    {subscription.interval === 'month' ? 'Monthly' : 'Yearly'}
                  </Text>
                </View>

                <View style={styles.detailRow}>
                  <Text style={styles.detailLabel}>Current Period</Text>
                  <Text style={styles.detailValue}>
                    {formatDate(subscription.currentPeriodStart)} - {formatDate(subscription.currentPeriodEnd)}
                  </Text>
                </View>

                {subscription.nextBillingDate && (
                  <View style={styles.detailRow}>
                    <Text style={styles.detailLabel}>Next Billing</Text>
                    <Text style={styles.detailValue}>
                      {formatDate(subscription.nextBillingDate)}
                    </Text>
                  </View>
                )}

                {subscription.cancelAtPeriodEnd && (
                  <View style={styles.cancellationNotice}>
                    <Text style={styles.cancellationText}>
                      ⚠️ Your subscription will be cancelled at the end of the current period.
                    </Text>
                  </View>
                )}
              </View>
            </View>

            {/* Action Buttons */}
            <View style={styles.actionsContainer}>
              {subscription.cancelAtPeriodEnd ? (
                <TouchableOpacity
                  style={[styles.actionButton, styles.reactivateButton]}
                  onPress={handleReactivateSubscription}
                  disabled={actionLoading}
                >
                  {actionLoading ? (
                    <ActivityIndicator size="small" color="white" />
                  ) : (
                    <Text style={styles.actionButtonText}>Reactivate Subscription</Text>
                  )}
                </TouchableOpacity>
              ) : (
                <>
                  <TouchableOpacity
                    style={[styles.actionButton, styles.upgradeButton]}
                    onPress={handleUpgradePlan}
                  >
                    <Text style={styles.actionButtonText}>Upgrade Plan</Text>
                  </TouchableOpacity>

                  <TouchableOpacity
                    style={[styles.actionButton, styles.cancelButton]}
                    onPress={handleCancelSubscription}
                    disabled={actionLoading}
                  >
                    {actionLoading ? (
                      <ActivityIndicator size="small" color="#FF3B30" />
                    ) : (
                      <Text style={[styles.actionButtonText, styles.cancelButtonText]}>
                        Cancel Subscription
                      </Text>
                    )}
                  </TouchableOpacity>
                </>
              )}
            </View>

            {/* Billing History */}
            <View style={styles.billingHistoryContainer}>
              <Text style={styles.sectionTitle}>Billing History</Text>
              <View style={styles.billingItem}>
                <View style={styles.billingItemLeft}>
                  <Text style={styles.billingItemTitle}>Premium Plan - January 2024</Text>
                  <Text style={styles.billingItemDate}>January 1, 2024</Text>
                </View>
                <View style={styles.billingItemRight}>
                  <Text style={styles.billingItemAmount}>$9.99</Text>
                  <Text style={styles.billingItemStatus}>Paid</Text>
                </View>
              </View>
              
              <View style={styles.billingItem}>
                <View style={styles.billingItemLeft}>
                  <Text style={styles.billingItemTitle}>Premium Plan - December 2023</Text>
                  <Text style={styles.billingItemDate}>December 1, 2023</Text>
                </View>
                <View style={styles.billingItemRight}>
                  <Text style={styles.billingItemAmount}>$9.99</Text>
                  <Text style={styles.billingItemStatus}>Paid</Text>
                </View>
              </View>
            </View>

            {/* Support */}
            <View style={styles.supportContainer}>
              <Text style={styles.sectionTitle}>Need Help?</Text>
              <Text style={styles.supportText}>
                If you have any questions about your subscription or need assistance, 
                please contact our support team.
              </Text>
              <TouchableOpacity style={styles.contactSupportButton}>
                <Text style={styles.contactSupportButtonText}>Contact Support</Text>
              </TouchableOpacity>
            </View>
          </>
        )}
      </View>
    </ScrollView>
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: '#F8F9FA',
  },
  loadingContainer: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
    backgroundColor: '#F8F9FA',
  },
  loadingText: {
    marginTop: 16,
    fontSize: 16,
    color: '#666',
  },
  header: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    paddingHorizontal: 20,
    paddingVertical: 15,
    paddingTop: 50,
    backgroundColor: '#007AFF',
  },
  backButton: {
    backgroundColor: 'rgba(255,255,255,0.2)',
    paddingHorizontal: 12,
    paddingVertical: 8,
    borderRadius: 20,
  },
  backButtonText: {
    color: 'white',
    fontSize: 14,
    fontWeight: '600',
  },
  headerTitle: {
    fontSize: 20,
    fontWeight: 'bold',
    color: 'white',
  },
  content: {
    padding: 20,
  },
  noSubscriptionContainer: {
    alignItems: 'center',
    paddingVertical: 40,
  },
  noSubscriptionTitle: {
    fontSize: 24,
    fontWeight: 'bold',
    color: '#333',
    marginBottom: 12,
  },
  noSubscriptionText: {
    fontSize: 16,
    color: '#666',
    textAlign: 'center',
    marginBottom: 24,
    lineHeight: 24,
  },
  browsePlansButton: {
    backgroundColor: '#007AFF',
    paddingHorizontal: 24,
    paddingVertical: 12,
    borderRadius: 8,
  },
  browsePlansButtonText: {
    color: 'white',
    fontSize: 16,
    fontWeight: '600',
  },
  subscriptionCard: {
    backgroundColor: 'white',
    borderRadius: 16,
    padding: 20,
    marginBottom: 24,
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 0.1,
    shadowRadius: 4,
    elevation: 3,
  },
  subscriptionHeader: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: 20,
  },
  planName: {
    fontSize: 24,
    fontWeight: 'bold',
    color: '#1C1C1E',
  },
  statusBadge: {
    paddingHorizontal: 12,
    paddingVertical: 6,
    borderRadius: 16,
  },
  statusText: {
    color: 'white',
    fontSize: 12,
    fontWeight: 'bold',
  },
  subscriptionDetails: {
    marginBottom: 16,
  },
  detailRow: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: 12,
  },
  detailLabel: {
    fontSize: 16,
    color: '#666',
  },
  detailValue: {
    fontSize: 16,
    fontWeight: '600',
    color: '#1C1C1E',
  },
  cancellationNotice: {
    backgroundColor: '#FFF3CD',
    padding: 12,
    borderRadius: 8,
    marginTop: 12,
    borderWidth: 1,
    borderColor: '#FFEAA7',
  },
  cancellationText: {
    fontSize: 14,
    color: '#856404',
    textAlign: 'center',
  },
  actionsContainer: {
    marginBottom: 24,
  },
  actionButton: {
    paddingVertical: 16,
    borderRadius: 8,
    alignItems: 'center',
    marginBottom: 12,
  },
  upgradeButton: {
    backgroundColor: '#007AFF',
  },
  reactivateButton: {
    backgroundColor: '#34C759',
  },
  cancelButton: {
    backgroundColor: 'transparent',
    borderWidth: 2,
    borderColor: '#FF3B30',
  },
  actionButtonText: {
    fontSize: 16,
    fontWeight: '600',
    color: 'white',
  },
  cancelButtonText: {
    color: '#FF3B30',
  },
  billingHistoryContainer: {
    marginBottom: 24,
  },
  sectionTitle: {
    fontSize: 20,
    fontWeight: 'bold',
    color: '#1C1C1E',
    marginBottom: 16,
  },
  billingItem: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    backgroundColor: 'white',
    padding: 16,
    borderRadius: 8,
    marginBottom: 8,
  },
  billingItemLeft: {
    flex: 1,
  },
  billingItemTitle: {
    fontSize: 16,
    fontWeight: '600',
    color: '#1C1C1E',
    marginBottom: 4,
  },
  billingItemDate: {
    fontSize: 14,
    color: '#666',
  },
  billingItemRight: {
    alignItems: 'flex-end',
  },
  billingItemAmount: {
    fontSize: 16,
    fontWeight: 'bold',
    color: '#1C1C1E',
    marginBottom: 4,
  },
  billingItemStatus: {
    fontSize: 12,
    color: '#34C759',
    fontWeight: '600',
  },
  supportContainer: {
    backgroundColor: 'white',
    padding: 20,
    borderRadius: 16,
    marginBottom: 20,
  },
  supportText: {
    fontSize: 16,
    color: '#666',
    lineHeight: 24,
    marginBottom: 16,
  },
  contactSupportButton: {
    backgroundColor: '#F8F9FA',
    paddingVertical: 12,
    paddingHorizontal: 20,
    borderRadius: 8,
    alignItems: 'center',
  },
  contactSupportButtonText: {
    fontSize: 16,
    fontWeight: '600',
    color: '#007AFF',
  },
});

export default UserSubscriptionManagementScreen;



